// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek
import {
  ActionType,
  AuthFlow,
  IAction,
  IActionTypeAuthFail,
  IActionTypeLoginResult,
  IActionChangeInput,
  IActionChangeOutput,
  IActionCommand,
  IActionTypeChangeUser,
  IActionCloudTrendData,
} from "actions";
import { Command, ImportFile } from "graphql/api";
import { AppState, IStateScheduler } from "states/schedulerstates";
import { IState } from "states/states";
import { DataState, MainPanel } from "types";
import { isSet } from "shared/utils/utils";
import { isLocalhost } from "guiutils";
import { FileUploadStatus } from "shared/types/cloudtypes";

// uses webpack definitions
declare const IS_CLOUD: boolean;

function forceReload(state: IStateScheduler) {
  // force immediate reloading of data
  state.allData = {};
  state.alarm = {};
  state.parameter = {};
  state.ocppChargerList = {};
  state.ocppConnectorData = {};
  state.ocppRfidListBasic = {};
  state.ocppRfidListExt = {};
  state.ocppTariffList = {};
  state.pendingOcppSessionState = {};
  state.trendLoading = {};
  state.pushRegister = {};
  // re-evaluate the proxy connection after a site change / reconnect
  state.liveViaProxy = false;
}

export default function schedulerReducer(
  state: IStateScheduler,
  action: IAction<any>,
  rootState: IState,
): IStateScheduler {
  switch (action.type) {
    case ActionType.ACT_APP_LOADED:
      if (IS_CLOUD || !isLocalhost()) {
        state.appState = AppState.LOGGED_OUT;
      } else {
        // on local embyt controller display we skip some states
        state.appState = AppState.SITE_SELECTED;
      }
      return state;

    case ActionType.ACT_VISIBILITY_CHANGE: {
      const isVisible = action.payload as boolean;
      state.isInBackground = !isVisible;
      return state;
    }

    case ActionType.ACT_GOTO_LOGIN:
      state.appState = AppState.LOGGED_OUT;
      state.loginDataState = DataState.CLEAN;
      return state;

    case ActionType.ACT_SIGNUP:
      state.loginDataState = DataState.CLEAN;
      return state;
    case ActionType.ACT_SIGNUP_CONFIRM:
      state.loginDataState = DataState.CLEAN;
      return state;
    case ActionType.ACT_SIGNUP_REQ:
    case ActionType.ACT_SIGNUP_CONFIRM_REQ:
    case ActionType.ACT_SIGNUP_RESEND_REQ:
      state.loginDataState = DataState.SAVE_REQ;
      return state;

    case ActionType.ACT_SIGNUP_SUCC:
      // we've got success answer from last call -> check in which state we are...
      state.loginDataState = DataState.SAVE_SUCC;

      return state;

    case ActionType.ACT_NAVIGATION:
      // if this is called, the blocker has done it's work -> so we can reset it savely
      state.saveState = DataState.CLEAN;
      return state;

    case ActionType.ACT_LOGIN_REQ:
      state.login.lastRequest = Date.now();
      state.loginDataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_LOGIN_SUCC: {
      const payload = action.payload as IActionTypeLoginResult;
      state.login.lastData = Date.now();
      state.loginDataState = DataState.SAVE_SUCC;

      if (!IS_CLOUD) {
        // embyt or LINX HW
        if (isLocalhost()) {
          // local login is optional
          state.appState = AppState.READY;
        } else {
          // no need to load site and user list and to select site
          state.appState = AppState.SITE_SELECTED;
        }
      } else {
        state.appState = AppState.LOGGED_IN;
      }
      return state;
    }
    case ActionType.ACT_AUTH_FAIL: {
      const payload = action.payload as IActionTypeAuthFail;
      if (payload.flow === AuthFlow.MFA_SETTINGS) {
        state.mfaDataState = DataState.SAVE_FAIL;
      } else {
        state.loginDataState = DataState.SAVE_FAIL;
        if (payload.flow === AuthFlow.LOGIN) {
          state.appState = AppState.LOGGED_OUT;
        }
      }
      return state;
    }

    case ActionType.ACT_LOGOUT_REQ:
      state.loginDataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_LOGOUT_SUCC:
      state.appState = AppState.LOGGED_OUT;
      state.loginDataState = DataState.SAVE_SUCC;
      state.siteList = {};
      state.userListBasic = {};
      state.companyList = {};
      state.reportList = {};
      // ensure reloading of site data
      forceReload(state);
      return state;

    case ActionType.ACT_FORGOT_PASSWORD:
      state.loginDataState = DataState.CLEAN;
      return state;
    case ActionType.ACT_BACK_TO_LOGIN:
      state.loginDataState = DataState.CLEAN;
      return state;
    case ActionType.ACT_FORGOT_PASSWORD_CODE_REQ:
      state.loginDataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_FORGOT_PASSWORD_CODE_SUCC:
      state.loginDataState = DataState.SAVE_SUCC;
      return state;

    case ActionType.ACT_FORGOT_PASSWORD_CHANGE_REQ:
      state.loginDataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_FORGOT_PASSWORD_CHANGE_SUCC:
      state.loginDataState = DataState.SAVE_SUCC;
      // maybe we should show some info on the LogIn screen, that we have changed the Password
      // and that the user should try to login again...
      return state;

    case ActionType.ACT_FORCE_PWD_CHANGE:
      state.loginDataState = DataState.CLEAN;
      return state;

    case ActionType.ACT_MFA_CODE_REQUIRED:
      state.loginDataState = DataState.CLEAN;
      return state;
    case ActionType.ACT_MFA_CONFIRM_REQ:
      state.loginDataState = DataState.SAVE_REQ;
      return state;

    // the MFA enrollment on the settings page uses its own data state to not interfere
    // with the password change form, which shares loginDataState
    case ActionType.ACT_MFA_STATUS_REQ: // intentional fall-through
    case ActionType.ACT_MFA_SETUP_REQ: // intentional fall-through
    case ActionType.ACT_MFA_VERIFY_REQ: // intentional fall-through
    case ActionType.ACT_MFA_DISABLE_REQ:
      state.mfaDataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_MFA_SETUP_SUCC: // intentional fall-through
    case ActionType.ACT_MFA_STATUS_SUCC:
      state.mfaDataState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_MFA_SETUP_CANCEL:
      state.mfaDataState = DataState.CLEAN;
      return state;
    case ActionType.ACT_CANCEL_PWD_CHANGE:
      state.loginDataState = DataState.CLEAN;
      return state;
    case ActionType.ACT_CHANGE_AWSUSER_REQ:
      state.loginDataState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_CHANGE_AWSUSER_SUCC:
      state.loginDataState = DataState.SAVE_SUCC;
      return state;

    case ActionType.ACT_CHANGE_USER_REQ: {
      const userId = (action.payload as IActionTypeChangeUser).id;
      state.saveState = DataState.SAVE_REQ;
      return state;
    }
    case ActionType.ACT_CHANGE_USER_SUCC:
      state.saveState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_CHANGE_USER_FAIL:
      state.saveState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_CHANGE_SITESETTINGS_REQ:
      state.saveState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_CHANGE_SITESETTINGS_SUCC:
      state.saveState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_CHANGE_SITESETTINGS_FAIL:
      state.saveState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_GET_SITES_REQ:
      state.siteList.lastRequest = Date.now();
      // do not (re)set parameter state here to keep error message present
      return state;

    case ActionType.ACT_GET_SITES_SUCC:
      state.siteList.lastData = Date.now();
      // switch state if we have site, user lists, and company list
      if (
        isSet(state.userListBasic.lastData) &&
        isSet(state.companyList.lastData) &&
        state.appState < AppState.SITE_LIST_LOADED
      ) {
        state.appState = AppState.SITE_LIST_LOADED;
      }
      return state;
    case ActionType.ACT_GET_USERLIST_BASIC_REQ:
      state.userListBasic.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_USERLIST_BASIC_SUCC:
      state.userListBasic.lastData = Date.now();
      // when we receive basic data, extended data gets lost
      state.userListExt.lastData = undefined;
      // except for RFID users, where we do not need the site list and we switch directly to site-selected
      if (rootState.site.user?.isRfidUser && state.appState < AppState.SITE_SELECTED) {
        state.appState = AppState.SITE_SELECTED;
      }
      // for non-rfid users, we switch state if we have site, user lists, and company list
      else if (
        isSet(state.siteList.lastData) &&
        isSet(state.companyList.lastData) &&
        state.appState < AppState.SITE_LIST_LOADED
      ) {
        state.appState = AppState.SITE_LIST_LOADED;
      }
      return state;
    case ActionType.ACT_GET_USERLIST_EXT_REQ:
      state.userListExt.lastRequest = Date.now();
      // we need to clear lastData to avoid indicating data ready although another user was loaded before
      state.userListExt.lastData = undefined;
      return state;
    case ActionType.ACT_GET_USERLIST_EXT_SUCC:
      state.userListExt.lastData = Date.now();
      return state;
    case ActionType.ACT_GET_COMPANIES_REQ:
      state.companyList.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_COMPANIES_SUCC:
      state.companyList.lastData = Date.now();
      // switch state if we have site, user lists, and company list
      if (
        isSet(state.siteList.lastData) &&
        isSet(state.userListBasic.lastData) &&
        state.appState < AppState.SITE_LIST_LOADED
      ) {
        state.appState = AppState.SITE_LIST_LOADED;
      }
      return state;

    case ActionType.ACT_SELECT_SITE:
      state.appState = AppState.SITE_SELECTED;
      state.parameterDataState = DataState.DIRTY;
      // allow immediate reloading of parameter data
      forceReload(state);
      return state;

    case ActionType.ACT_DISCONNECT_SITE:
      if (state.appState >= AppState.SITE_SELECTED) {
        state.appState = AppState.SITE_LIST_LOADED;
      }
      state.trendLoadingState = DataState.EMPTY;
      forceReload(state);
      return state;

    case ActionType.ACT_LIVE_DATA_SUBSCRIBED:
      state.appState = AppState.LIVE_DATA_SUBSCRIBED;
      return state;

    case ActionType.ACT_GET_PARAMETER_REQ:
      state.parameter.lastRequest = Date.now();
      // do not (re)set parameter state here to keep error message present
      return state;
    case ActionType.ACT_GET_PARAMETER_SUCC:
      state.parameter.lastData = Date.now();
      state.parameterDataState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_GET_PARAMETER_FAIL:
      state.parameterDataState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_GET_OCPP_CHARGERS_REQ:
      state.ocppChargerList.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_OCPP_CHARGERS_SUCC:
      state.ocppChargerList.lastData = Date.now();
      return state;
    case ActionType.ACT_GET_OCPP_CONNECTORS_REQ:
      state.ocppConnectorData.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_OCPP_CONNECTORS_SUCC:
      state.ocppConnectorData.lastData = Date.now();
      return state;

    case ActionType.ACT_GET_OCPP_RFIDS_BASIC_REQ:
      state.ocppRfidListBasic.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_OCPP_RFIDS_BASIC_SUCC:
      state.ocppRfidListBasic.lastData = Date.now();
      // when we receive basic RFID data, extended data gets lost
      state.ocppRfidListExt.lastData = undefined;
      return state;
    case ActionType.ACT_GET_OCPP_RFIDS_EXT_REQ:
      state.ocppRfidListExt.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_OCPP_RFIDS_EXT_SUCC:
      state.ocppRfidListExt.lastData = Date.now();
      return state;

    case ActionType.ACT_GET_OCPP_SITE_TARIFFS_REQ:
      state.ocppTariffList.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_OCPP_SITE_TARIFFS_SUCC:
      state.ocppTariffList.lastData = Date.now();
      return state;

    case ActionType.ACT_GET_ALARMS_REQ:
      state.alarm.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_ALARMS_SUCC:
      state.alarm.lastData = Date.now();
      return state;

    case ActionType.ACT_GET_CONTROLLERDATA_REQ:
      state.controllerData.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_CONTROLLERDATA_SUCC:
      state.controllerData.lastData = Date.now();
      return state;

    case ActionType.ACT_SET_LIVE_VIA_PROXY:
      state.liveViaProxy = action.payload as boolean;
      return state;

    case ActionType.ACT_GET_ALLDATA_REQ:
      state.allData.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_ALLDATA_SUCC:
    case ActionType.ACT_GET_CLOUDLIVEDATA_SUCC:
      state.allData.lastData = Date.now();
      return state;

    case ActionType.ACT_REGISTER_FOR_PUSH_DATA_REQ:
      // in cloud, registering for live data equals a request for it (unless polled via proxy)
      if (!state.liveViaProxy) {
        state.allData.lastRequest = Date.now();
      }
      // track the registration cadence separately from the proxy live-data poll
      state.pushRegister.lastRequest = Date.now();
      return state;
    case ActionType.ACT_REGISTER_FOR_PUSH_DATA_SUCC:
      state.isOffline = false;
      return state;
    case ActionType.ACT_REGISTER_FOR_PUSH_DATA_FAIL:
      state.isOffline = true;
      return state;
    case ActionType.ACT_REGISTER_FOR_PUSH_DATA_TIMEOUT:
      // we have been offline for long time, so we need to reload all data
      forceReload(state);
      return state;

    case ActionType.ACT_PARAMETER_LOADED:
      if (state.appState < AppState.PARAMETER_LOADED) {
        state.appState = AppState.PARAMETER_LOADED;
      }
      return state;

    case ActionType.ACT_INIT_DONE:
      state.appState = AppState.READY;
      // we can immediately start trend loading and do not need to wait for trend info
      // no trend loading if we face a site without parameters, i.e. a virtual ocpp charger site
      if (isSet(state.parameter.lastData)) {
        state.trendLoadingState = DataState.SAVE_REQ;
      }
      return state;

    case ActionType.ACT_SET_PAGE_DIRTY:
      if (
        (!isSet(action.payload) || action.payload === true) &&
        state.saveState !== DataState.SAVE_REQ
      ) {
        state.saveState = DataState.DIRTY;
      }
      return state;

    case ActionType.ACT_MODAL_CANCEL:
      // we reset save status
      if (rootState.router.panel !== MainPanel.SITES) {
        state.saveState = DataState.CLEAN;
      }
      return state;

    case ActionType.ACT_GET_TREND_REQ:
      state.trendLoading.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_CLOUDTREND_SUCC: {
      const payload = action.payload as IActionCloudTrendData;
      state.trendLoadingProgress = payload.progress;
      if (payload.progress === 1) {
        // finished now!
        state.trendLoading.lastData = Date.now();
        state.allData.lastRequest = undefined;
        state.trendLoadingState = DataState.SAVE_SUCC;
      }
      return state;
    }
    case ActionType.ACT_GET_CHARGERTREND_SUCC:
      state.trendLoading.lastData = Date.now();
      state.chargingSession.lastData = Date.now();
      // we get all data in a single request
      state.trendLoadingState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_APPEND_CLOUDTREND:
      state.trendLoading.lastData = Date.now();
      return state;
    case ActionType.ACT_CHANGE_TREND_SUCC:
      state.trendLoading.lastData = Date.now();
      return state;
    case ActionType.ACT_GET_TREND_FAIL:
      // we stop trend loading on such an event to avoid endless loops
      state.trendLoadingState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_GET_IOTREND_REQ:
      state.ioTrendLoading.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_CLOUDIOTREND_SUCC:
      state.ioTrendLoading.lastData = Date.now();
      // we get all data in a single request
      state.isIoTrendLoading = false;
      return state;
    case ActionType.ACT_GET_CLOUDIOTREND_FULL_SUCC:
      state.ioTrendLoading.lastData = Date.now();
      return state;
    case ActionType.ACT_GET_IOTREND_FAIL:
      // we stop trend loading on such an event to avoid endless loops
      state.isIoTrendLoading = false;
      return state;

    case ActionType.ACT_UPDATE_TREND:
      state.trendLoadingState = DataState.SAVE_REQ;
      state.trendLoadingProgress = 0;
      // ensure to invoke new request immediately
      state.trendLoading.lastRequest = undefined;
      return state;
    case ActionType.ACT_UPDATE_IOTREND:
      state.isIoTrendLoading = true;
      // ensure to invoke new request immediately
      state.ioTrendLoading.lastRequest = undefined;
      return state;

    case ActionType.ACT_CHANGE_INPUT_REQ: {
      const payload = action.payload as IActionChangeInput;
      state.pendingCommand = {
        timestampMs: Date.now(),
        seqNr: payload.commandSeqNr,
        command: Command.setInput,
        cmdArgs: payload,
      };
      return state;
    }
    case ActionType.ACT_CHANGE_INPUT_FAIL:
      state.pendingCommand = undefined; // clear pending command
      return state;
    case ActionType.ACT_CHANGE_INPUT_SUCC:
      state.pendingCommand = undefined; // clear pending command
      return state;

    case ActionType.ACT_CHANGE_OUTPUT_REQ: {
      const payload = action.payload as IActionChangeOutput;
      state.pendingCommand = {
        timestampMs: Date.now(),
        seqNr: payload.commandSeqNr,
        command: Command.setOutput,
        cmdArgs: payload,
      };
      return state;
    }
    case ActionType.ACT_CHANGE_OUTPUT_FAIL:
      state.pendingCommand = undefined; // clear pending command
      return state;
    case ActionType.ACT_CHANGE_OUTPUT_SUCC:
      state.pendingCommand = undefined; // clear pending command
      state.setOutput.lastRequest = Date.now();
      return state;

    case ActionType.ACT_OPEN_SSH_TUNNEL_REQ: {
      const payload = action.payload as IActionCommand;
      state.pendingCommand = {
        timestampMs: Date.now(),
        seqNr: payload.commandSeqNr,
        command: Command.openSshTunnel,
        cmdArgs: null,
      };
      // clear previous fail flag, but do not reset data-changed flag
      if (state.saveState === DataState.SAVE_FAIL) {
        state.saveState = DataState.EMPTY;
      }
      return state;
    }
    case ActionType.ACT_OPEN_SSH_TUNNEL_SUCC:
      state.pendingCommand = undefined; // clear pending command
      return state;
    case ActionType.ACT_OPEN_SSH_TUNNEL_FAIL:
      state.pendingCommand = undefined; // clear pending command
      state.saveState = DataState.SAVE_FAIL; // indicate failure, even if save state is not used for cmd
      return state;

    case ActionType.ACT_REBOOT_REQ: {
      const payload = action.payload as IActionCommand;
      state.pendingCommand = {
        timestampMs: Date.now(),
        seqNr: payload.commandSeqNr,
        command: Command.rebootController,
        cmdArgs: null,
      };
      // clear previous fail flag, but do not reset data-changed flag
      if (state.saveState === DataState.SAVE_FAIL) {
        state.saveState = DataState.EMPTY;
      }
      return state;
    }
    case ActionType.ACT_REBOOT_SUCC:
      state.pendingCommand = undefined; // clear pending command
      return state;
    case ActionType.ACT_REBOOT_FAIL:
      state.pendingCommand = undefined; // clear pending command
      state.saveState = DataState.SAVE_FAIL; // indicate failure, even if save state is not used for cmd
      return state;

    case ActionType.ACT_GET_PENDING_OCPP_SESSIONS_REQ:
      state.pendingOcppSessionState.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_PENDING_OCPP_SESSIONS_SUCC:
      state.pendingOcppSessionState.lastData = Date.now();
      return state;
    case ActionType.ACT_NEW_CHARGING_SESSION_DATA:
      // live/push data for new session info should also update
      state.pendingOcppSessionState.lastData = Date.now();
      return state;

    case ActionType.ACT_GET_OCPP_SESSIONS_REQ:
      state.ocppSessionState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_GET_OCPP_SESSIONS_SUCC:
      state.ocppSessionState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_GET_OCPP_SESSIONS_FAIL:
      state.ocppSessionState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_SELECT_CHARGING_SESSION:
      state.chargingPower.lastRequest = Date.now();
      state.chargingSession.lastRequest = Date.now();
      return state;

    case ActionType.ACT_EDIT_REPORT: // intentional fall-through
    case ActionType.ACT_EDIT_TRENDMONITOR:
    case ActionType.ACT_EDIT_ALARM_NOTIFICATION:
      state.saveState = DataState.CLEAN;
      return state;
    case ActionType.ACT_SAVE_REPORTS_SETTINGS_REQ:
      state.saveState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_SAVE_REPORT_SETTINGS_SUCC:
      state.saveState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_SAVE_REPORT_SETTINGS_FAIL:
      state.saveState = DataState.SAVE_FAIL;
      return state;
    case ActionType.ACT_GET_REPORTS_REQ:
      state.reportList.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_REPORTS_SUCC:
      state.reportList.lastData = Date.now();
      return state;
    case ActionType.ACT_CHANGE_REPORT_REQ:
      state.saveState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_CHANGE_REPORT_SUCC:
      state.saveState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_CHANGE_REPORT_FAIL:
      state.saveState = DataState.SAVE_FAIL;
      return state;
    case ActionType.ACT_CHANGE_ALARM_NOTIFICATION_REQ:
      state.saveState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_CHANGE_ALARM_NOTIFICATION_SUCC:
      state.saveState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_CHANGE_ALARM_NOTIFICATION_FAIL:
      state.saveState = DataState.SAVE_FAIL;
      return state;
    case ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_REQ:
      state.saveState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_SUCC:
      state.saveState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_SAVE_TRENDMONITOR_SETTINGS_FAIL:
      state.saveState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_GET_CHARGER_POWER_SUCC:
      state.chargingPower.lastData = Date.now();
      break;
    case ActionType.ACT_SAVE_CHARGER_POWER_REQ:
      state.saveState = DataState.SAVE_REQ;
      return state;
    case ActionType.ACT_SAVE_CHARGER_POWER_SUCC:
      state.saveState = DataState.SAVE_SUCC;
      return state;
    case ActionType.ACT_SAVE_CHARGER_POWER_FAIL:
      state.saveState = DataState.SAVE_FAIL;
      return state;

    case ActionType.ACT_PERFORM_FILE_UPLOAD_SUCC:
      state.isFileUploadLoading = true;
      return state;
    case ActionType.ACT_GET_FILEUPLOADS_REQ:
      state.fileUploadLoading.lastRequest = Date.now();
      return state;
    case ActionType.ACT_GET_FILEUPLOADS_SUCC: {
      const payload = [...(action.payload as ImportFile[])];

      const lastUpload =
        payload.length > 0 ? payload.sort((a, b) => a.addedOn - b.addedOn).pop() : undefined;

      state.isFileUploadLoading =
        isSet(lastUpload) &&
        [FileUploadStatus.INITIAL, FileUploadStatus.RECEIVED].includes(
          lastUpload.status as FileUploadStatus,
        );

      return state;
    }
  }
  return state;
}
